import { TCmsDocument } from '@daymade/api-contracts';
import clsx from 'clsx';
import AboutUsModule from 'components/about-us/AboutUsModule';
import AboutUsSiteContainer from 'components/about-us/AboutUsSiteContainer';
import AspectRatioContainer from 'components/about-us/AspectRatioContainer';
import EmblaCarousel from 'components/carousel2025/EmblaCarousel';
import { FadeIn } from 'components/common/FadeIn';
import { EnterButton } from 'components/home-page/enter-button/EnterButton';
import ImageWithFallback from 'components/ImageWithFallback';
import AutoScroll from 'embla-carousel-auto-scroll';
import useEmblaCarousel from 'embla-carousel-react';
import { useDrawStore } from 'hooks/stores/useDrawStore';
import { useUserStore } from 'hooks/stores/useUserStore';
import { observer } from 'mobx-react-lite';
import Image from 'next/image';
import Link from 'next/link';
import { useEffect, useRef, useState } from 'react';
import { tryApi } from 'utils/api/common';
import { cmsApiClient } from 'utils/apiClient';
import {
  isReviewType,
  isWinnerPolaroidType,
  isWinnerType,
} from 'utils/cms/helpers';
import HttpStatusCode from 'utils/HttpStatusCode';
import TrustPilot45WithTrustPilotLogo from '../../../components/svg/Trustpilot/TrustPilot45WithTrustPilotLogo.svg';
import TrustpilotReviews from '../../svg/Trustpilot/trustpilot-reviews.svg';
import WinnersBottomWaveDesktop from '../../svg/Waves/winners-bottom-wave-desktop.svg';
import WinnersBottomWaveMobile from '../../svg/Waves/winners-bottom-wave-mobile.svg';
import WinnersTopWaveDesktop from '../../svg/Waves/winners-top-wave-desktop.svg';
import WinnersTopWaveMobile from '../../svg/Waves/winners-top-wave-mobile.svg';
import styles from './Winners.module.css';

const Winners = observer(() => {
  const userStore = useUserStore();
  const drawStore = useDrawStore();

  const [polaroids, setPolaroids] = useState<TCmsDocument[]>();
  const [winnerReviews, setWinnerReviews] = useState<TCmsDocument[]>();

  const [hasScrolledIntoView, setHasScrolledIntoView] = useState(false);
  const carouselContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasScrolledIntoView) {
          setHasScrolledIntoView(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 },
    );

    if (carouselContainerRef.current) {
      observer.observe(carouselContainerRef.current);
    }

    return () => observer.disconnect();
  }, [hasScrolledIntoView]);

  const setAndSortWinnerReviews = (
    winners: TCmsDocument[],
    reviews: TCmsDocument[],
  ) => {
    const sortedWinners = [...winners].sort(
      (a, b) => a.sortOrder - b.sortOrder,
    );
    const sortedReviews = [...reviews].sort(
      (a, b) => a.sortOrder - b.sortOrder,
    );

    const alternated: TCmsDocument[] = [];
    let winnerIndex = 0;
    let reviewIndex = 0;

    // Alternate between reviews and winners
    while (
      reviewIndex < sortedReviews.length ||
      winnerIndex < sortedWinners.length
    ) {
      if (reviewIndex < sortedReviews.length) {
        alternated.push(sortedReviews[reviewIndex++]);
      }
      if (winnerIndex < sortedWinners.length) {
        alternated.push(sortedWinners[winnerIndex++]);
      }
    }

    setWinnerReviews(alternated);
  };

  useEffect(() => {
    (async () => {
      const polaroids = await tryApi(cmsApiClient.public.listWinnerPolaroids());

      if (polaroids.status === HttpStatusCode.OK) {
        setPolaroids(polaroids.body);
      }

      const winners = await tryApi(cmsApiClient.public.listWinners());
      const reviews = await tryApi(cmsApiClient.public.listReviews());

      if (
        winners.status === HttpStatusCode.OK &&
        reviews.status === HttpStatusCode.OK
      ) {
        setAndSortWinnerReviews(winners.body, reviews.body);
      }
    })();
  }, []);

  const emblaCarouselInfo = useEmblaCarousel(
    {
      loop: true,
      dragFree: true,
      inViewThreshold: 0.1,
      align: 'center',
      containScroll: false,
    },
    [AutoScroll({ playOnInit: true, speed: 0.6, stopOnInteraction: false })],
  );

  const showEnterButton = !userStore.subscriptionStatusInfo.subscriberStatus;

  return (
    <div>
      <div
        className={clsx(styles.winnersTopWave, styles.winnersTopWaveDesktop)}
      >
        <WinnersTopWaveDesktop />
      </div>
      <div className={clsx(styles.winnersTopWave, styles.winnersTopWaveMobile)}>
        <WinnersTopWaveMobile />
      </div>

      <AboutUsModule background="darkBlue">
        <AboutUsSiteContainer styles2026>
          <div className={styles.container}>
            <div className={styles.leftColumn}>
              <FadeIn onScroll className={styles.introHeading}>
                <p className="daymadeTypographyTaglineAccent">
                  Real people, real wins
                </p>
                <h2 className="daymadeTypographyXL">
                  Thousands of weekly winners
                </h2>
              </FadeIn>
              <FadeIn onScroll className={styles.trustpilotContainer}>
                <Link href="https://uk.trustpilot.com/review/daymade.co.uk">
                  <TrustPilot45WithTrustPilotLogo />
                </Link>
              </FadeIn>
              {showEnterButton && (
                <FadeIn onScroll className={styles.buttonContainer}>
                  <EnterButton
                    overrideButtonText="Join Winners"
                    overrideReferralText
                    dm2026Button
                    buttonSize="Large"
                  />
                </FadeIn>
              )}
            </div>
            <div className={styles.rightColumn}>
              <FadeIn onScroll className={styles.carouselContainer}>
                <div ref={carouselContainerRef}>
                  <AspectRatioContainer useCssVariableSizes>
                    <EmblaCarousel
                      className={styles.carousel}
                      stretchItemHeight
                      emblaCarouselInfo={emblaCarouselInfo}
                      pips2026
                      autoScroll={hasScrolledIntoView}
                      scaleCarousel={true}
                      items={
                        winnerReviews
                          ?.map((item) => {
                            const { data } = item;

                            if (isWinnerType(data)) {
                              return (
                                <div className={clsx(styles.carouselItem)}>
                                  <div className={styles.carouselImageWrapper}>
                                    <ImageWithFallback
                                      layout="fill"
                                      objectFit="cover"
                                      src={{
                                        src: data.image || '',
                                        width: 600,
                                        height: 600,
                                      }}
                                      alt={`Winner image of ${data.prizeName}`}
                                      className={styles.carouselImage}
                                    />
                                  </div>
                                  <div className={styles.prizeInfo}>
                                    <div className="daymadeTypographySRegular">
                                      {data.winnerName}
                                    </div>
                                    <div className="daymadeTypographyLBlack">
                                      {data.prizeName}
                                    </div>
                                  </div>
                                  <div
                                    className={styles.carouselItemGradient}
                                  />
                                </div>
                              );
                            }

                            if (isReviewType(data)) {
                              return (
                                <div
                                  className={clsx(
                                    styles.carouselItem,
                                    styles.review,
                                  )}
                                >
                                  <div
                                    className={clsx(
                                      'daymadeTypographySRegular',
                                      styles.reviewAuthor,
                                    )}
                                  >
                                    <div>{data.author}</div>
                                    <TrustpilotReviews />
                                  </div>
                                  <div
                                    className={clsx(
                                      'daymadeTypographyMRegular',
                                      styles.reviewContent,
                                    )}
                                  >
                                    "{data.content}"
                                  </div>
                                </div>
                              );
                            }

                            return null;
                          })
                          .filter(Boolean) ?? []
                      }
                    ></EmblaCarousel>
                  </AspectRatioContainer>
                </div>
              </FadeIn>
              <FadeIn onScroll className={styles.polaroidContainer}>
                <p className="daymadeTypographyMRegular">
                  And{' '}
                  <strong>{drawStore.uniqueWinners.toLocaleString()}+</strong>{' '}
                  more winners just like them
                </p>
                <Link href="/winners" className={styles.polaroids}>
                  {polaroids?.map((item) => {
                    const { data } = item;

                    if (isWinnerPolaroidType(data)) {
                      return (
                        <Polaroid
                          imageSrc={data.image}
                          name={data.winnerName}
                        />
                      );
                    }

                    return null;
                  })}
                </Link>
              </FadeIn>
            </div>
          </div>
        </AboutUsSiteContainer>
      </AboutUsModule>

      <div
        className={clsx(
          styles.winnersBottomWave,
          styles.winnersBottomWaveDesktop,
        )}
      >
        <WinnersBottomWaveDesktop />
      </div>
      <div
        className={clsx(
          styles.winnersBottomWave,
          styles.winnersBottomWaveMobile,
        )}
      >
        <WinnersBottomWaveMobile />
      </div>
    </div>
  );
});

interface PolaroidProps {
  name?: string;
  imageSrc?: string | null;
}
const Polaroid = (props: PolaroidProps) => {
  if (!props.name || !props.imageSrc) {
    return <></>;
  }
  return (
    <figure className={styles.polaroid}>
      <Image
        src={props.imageSrc}
        alt={`A photo of ${props.name}`}
        sizes="auto"
        width="50"
        height="50"
      />
      <figcaption className="aboutUsTypographyCaption">{props.name}</figcaption>
    </figure>
  );
};

export default Winners;
